import axios, { type AxiosInstance } from 'axios'
import type { AxiosRequestConfig } from 'axios'

import { i18n } from '@/plugins/i18n'
import { DEFAULT_LOCALE, PREFIX_LOCALES, type Locale } from '@/router/locales'
import { useUIStore } from '@/stores/ui.store'
import { normalizeLocale } from '@/utils/locale'

const API_ROOT = import.meta.env.VITE_API_URL
const WORDPRESS_URL = import.meta.env.VITE_WORDPRESS_URL
const FRONTEND_URL = import.meta.env.VITE_OG_URL

function getFrontendOrigin(): string | null {
  if (typeof FRONTEND_URL !== 'string' || !FRONTEND_URL.trim()) return null

  try {
    return new URL(FRONTEND_URL).origin
  } catch {
    return null
  }
}

function toError(error: unknown) {
  return error instanceof Error ? error : new Error(String(error))
}

function getErrorStatus(error: unknown) {
  return axios.isAxiosError(error) ? error.response?.status : undefined
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error)
}

function isBackendUnavailableError(error: unknown) {
  if (!axios.isAxiosError(error)) return false
  const status = error.response?.status

  if (typeof status === 'number') {
    return status >= 500 && status <= 599
  }

  return Boolean(error.request)
}

function reportBackendAvailabilityError(error: unknown) {
  if (!isBackendUnavailableError(error)) return

  try {
    const ui = useUIStore()
    ui.reportBackendUnavailable({
      status: getErrorStatus(error),
      message: getErrorMessage(error),
    })
  } catch {
    // Pinia may not be active during early module initialization.
  }
}

function isSupportedLocale(value: string): value is Locale {
  return ([DEFAULT_LOCALE, ...PREFIX_LOCALES] as readonly string[]).includes(value)
}

function isPrefixLocale(value: string): value is Exclude<Locale, typeof DEFAULT_LOCALE> {
  return (PREFIX_LOCALES as readonly string[]).includes(value)
}

export function resolveWpRequestLocale(): Locale {
  if (typeof window !== 'undefined') {
    const routeLocale = normalizeLocale(window.location.pathname.split('/')[1] ?? '')

    if (isPrefixLocale(routeLocale)) {
      return routeLocale
    }

    return DEFAULT_LOCALE
  }

  const activeLocale = normalizeLocale(i18n.global.locale.value)

  if (isSupportedLocale(activeLocale)) {
    return activeLocale
  }

  return DEFAULT_LOCALE
}

export function withWpLanguageParam<T extends AxiosRequestConfig>(config: T): T {
  const lang = resolveWpRequestLocale()
  const params = config.params

  if (params instanceof URLSearchParams) {
    if (!params.has('lang')) {
      params.set('lang', lang)
    }

    return {
      ...config,
      params,
    }
  }

  return {
    ...config,
    params: {
      lang,
      ...(params && typeof params === 'object' && !Array.isArray(params)
        ? (params as Record<string, unknown>)
        : {}),
    },
  }
}

export const wp: AxiosInstance = axios.create({
  baseURL: `${API_ROOT}/wp/v2/`,
  timeout: 20_000,
})

export const previewApi: AxiosInstance = axios.create({
  baseURL: WORDPRESS_URL,
  timeout: 20_000,
  withCredentials: false,
})

export const ig: AxiosInstance = axios.create({
  baseURL: `${API_ROOT}/ig-cache/v1`,
  timeout: 20_000,
})

export const noticesApi: AxiosInstance = axios.create({
  baseURL: `${API_ROOT}/awww/v1/`,
  timeout: 20_000,
})

// Example: interceptors (optional)
wp.interceptors.request.use(
  (config) => {
    const localizedConfig = withWpLanguageParam(config)

    // The API varies its validators by the allowed frontend origin. Browsers
    // add this header automatically; the SSG build needs to provide it so the
    // generated Pinia snapshot can carry the same ETags into hydration.
    if (typeof window === 'undefined' && !localizedConfig.headers.has('Origin')) {
      const frontendOrigin = getFrontendOrigin()
      if (frontendOrigin) {
        localizedConfig.headers.set('Origin', frontendOrigin)
      }
    }

    return localizedConfig
  },
  (error) => Promise.reject(toError(error)),
)

wp.interceptors.response.use(
  (res) => {
    return res
  },
  (error) => {
    reportBackendAvailabilityError(error)
    return Promise.reject(toError(error))
  },
)

previewApi.interceptors.response.use(
  (res) => {
    return res
  },
  (error) => {
    reportBackendAvailabilityError(error)
    return Promise.reject(toError(error))
  },
)

ig.interceptors.response.use(
  (res) => res,
  (error) => {
    // You can normalize errors here if needed.
    return Promise.reject(toError(error))
  },
)

noticesApi.interceptors.request.use(
  (config) => withWpLanguageParam(config),
  (error) => Promise.reject(toError(error)),
)

noticesApi.interceptors.response.use(
  (res) => {
    return res
  },
  (error) => {
    reportBackendAvailabilityError(error)
    return Promise.reject(toError(error))
  },
)
